import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useOrganizationStore } from '@/stores/organizationStore';
import { usePanelScroll } from '@/hooks/usePanelScroll';
import { useEditTelemetry } from '@/hooks/useEditTelemetry';
import { apiService } from '@/services/api';
import { 
  ClinicalValidationData, 
  ValidationUIState
} from '@/types/clinicalValidation';

// Components
import { TranscriptViewer } from '@/components/clinical/TranscriptViewer';
import { AudioPlayer, AudioPlayerRef } from '@/components/clinical/AudioPlayer';
import { MedicalNotePreview } from '@/components/clinical/MedicalNotePreview';
import { EnhancedCodingAssistant } from '@/components/clinical/EnhancedCodingAssistant';
import { ClinicalSummaryPanel } from '@/components/clinical/ClinicalSummaryPanel';
import { EhrDeliveryConfirmModal } from '@/components/clinical/EhrDeliveryConfirmModal';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import LoadingSpinner from '@/components/LoadingSpinner';
import RecordAccessDenied from '@/components/RecordAccessDenied';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { ScrollToTopButton } from '@/components/ui/ScrollToTopButton';
import { ClinicalSummary, ClinicalSummaryData } from '@/types/clinicalSummary';
import { SPEAKER_ROLE_CONFIG, normalizeSpeakerRole } from '@/utils/speakerRoleConfig';
import toast from 'react-hot-toast';

import {
  RefreshCw,
  AlertTriangle,
  ArrowLeft,
  FileText,
  Activity,
  Clock,
  MessageSquare,
  CheckCircle2,
  User,
  Calendar,
  Info,
  ChevronDown,
  Send
} from 'lucide-react';
import { notesService, type EhrDeliveryState } from '@/services/notes';
import { MedicalNoteMetadata } from '@/types/medicalNote';
import { useCodingAssistantStore } from '@/stores/codingAssistantStore';
import { deriveSectionStatus } from '@/utils/clinicalSection';
import { isSentinelDate, formatEhrDate, formatEhrTime, EHR_PRE_LOCALIZED_SYSTEMS } from '@/utils/dateFormat';
import { useSpeakerCorrectionStore } from '@/stores/speakerCorrectionStore';
import { SpeakerReviewPanel } from '@/components/clinical/SpeakerReviewPanel';
import { SignBlockModal } from '@/components/clinical/SignBlockModal';
import { AutoCorrectBanner } from '@/components/clinical/AutoCorrectBanner';
import { useSpeakerAnalysis } from '@/hooks/useSpeakerAnalysis';
import { isRecordAccessDenied } from '@/utils/recordAccess';

// Mobile optimization components
import { type PanelId } from '@/components/ui/SwipeablePanelContainer';


/**
 * Appointment mode only: whether the visit has a transcript to validate yet. A recording can be
 * uploaded while its transcription is still running (or after it failed); summary and note
 * generation need the transcript, so the page waits instead of firing them.
 */
type TranscriptionGate = 'ready' | 'not_recorded' | 'processing' | 'failed';

function transcriptionGateFor(appointment: any, transcript: any): TranscriptionGate {
  if (transcript?.content || transcript?.segments?.length) return 'ready';
  // An API without the statuses: keep the previous behavior
  if (!appointment?.transcription) return 'ready';
  const recordingStatus = appointment.recording?.status;
  const transcriptionStatus = appointment.transcription.status;
  if (transcriptionStatus === 'failed' || recordingStatus === 'failed') return 'failed';
  if (transcriptionStatus === 'completed') return 'ready';
  if (recordingStatus !== 'completed') return 'not_recorded';
  return 'processing';
}

/** How often the page re-reads a queued EHR delivery. */
const EHR_DELIVERY_POLL_MS = 15_000;

export const ClinicalValidation: React.FC = () => {
  const { appointmentId, encounterId } = useParams<{ appointmentId?: string; encounterId?: string }>();
  const navigate = useNavigate();
  const { user, isReadOnly, canEditClinical } = useAuth();
  const canRecord = useOrganizationStore((s) => s.canRecord);

  // Determine which ID to use for API calls (appointmentId takes precedence for backward compatibility)
  const resourceId = appointmentId || encounterId;
  const isEncounterMode = !!encounterId && !appointmentId;

  // Edit telemetry — fires fire-and-forget breadcrumbs; failures are silently swallowed by the hook
  const telemetry = useEditTelemetry(resourceId ?? '');

  // Audio playback ref
  const audioPlayerRef = useRef<AudioPlayerRef>(null);

  // Core state
  const [validationData, setValidationData] = useState<ClinicalValidationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [transcriptionGate, setTranscriptionGate] = useState<TranscriptionGate>('ready');
  const [checkingTranscript, setCheckingTranscript] = useState(false);

  // Medical codes state for button enablement
  const [approvedCodes, setApprovedCodes] = useState<any[]>([]);

  // Clinical summary state
  const [clinicalSummaryData, setClinicalSummaryData] = useState<ClinicalSummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Note regeneration tracking - detect when summary changes after note was generated
  const [summaryLastSaved, setSummaryLastSaved] = useState<string | null>(null);
  const [noteGeneratedAt, setNoteGeneratedAt] = useState<string | null>(null);

  // Derived state: summary has changes that aren't reflected in the note yet
  const hasPendingNoteChanges = summaryLastSaved !== null &&
    noteGeneratedAt !== null &&
    new Date(summaryLastSaved) > new Date(noteGeneratedAt);

  // EHR delivery state
  const [noteId, setNoteId] = useState<string | null>(null);
  const [noteStatus, setNoteStatus] = useState<string | null>(null);
  // null until the availability check answers, so neither the EHR action nor Sign note flashes
  const [ehrDeliveryAvailable, setEhrDeliveryAvailable] = useState<boolean | null>(null);
  const [finishing, setFinishing] = useState(false);
  // Where the note's delivery stands, read from the server (AMP-109). Null until it answers.
  const [ehrDelivery, setEhrDelivery] = useState<EhrDeliveryState | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Standalone signing: an appointment with nowhere to deliver the note is finished by signing it
  const [signing, setSigning] = useState(false);
  const [showSignConfirm, setShowSignConfirm] = useState(false);
  // In a standalone practice the provider signs; an admin signs only while the standalone
  // recording rule is switched off (canRecord true). The API enforces the same rule.
  const canSignStandalone = !isEncounterMode && ehrDeliveryAvailable === false && canEditClinical()
    && (user?.role === 'operator' || canRecord === true);

  // UI state
  const [uiState, setUIState] = useState<ValidationUIState>({
    selectedEntityId: undefined,
    highlightedSegmentId: undefined,
    editingEntityId: undefined,
    filterBy: 'all',
    sortBy: 'confidence',
    sortOrder: 'desc',
    showConflicts: true,
    showSafetyAlerts: true,
    autoSave: true
  });

  
  // Responsive state - 4-tier breakpoint detection
  const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'smallDesktop' | 'desktop'>(() => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    if (width < 1280) return 'smallDesktop';
    return 'desktop';
  });
  const [showMobilePanel, setShowMobilePanel] = useState<PanelId>('soap');
  const [rightPanelView, setRightPanelView] = useState<'note' | 'coding'>('note');

  // Derived responsive helpers
  const isMobile = screenSize === 'mobile';
  const isTablet = screenSize === 'tablet';
  const isSmallDesktop = screenSize === 'smallDesktop';
  const isDesktop = screenSize === 'desktop';
  const isTabletOrSmallDesktop = isTablet || isSmallDesktop;

  // Mobile compact header - expandable patient details
  const [showPatientDetails, setShowPatientDetails] = useState(false);

  // Transcript panel view state - toggle between transcript and clinical summary
  // Defaults to summary so providers land on the AI-generated note without an extra click
  const [transcriptView, setTranscriptView] = useState<'transcript' | 'summary'>('summary');

  // Panel scroll hooks
  const transcriptScroll = usePanelScroll({ panelId: 'transcript' });
  const entitiesScroll = usePanelScroll({ panelId: 'entities' });
  const sidebarScroll = usePanelScroll({ panelId: 'sidebar' });

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) setScreenSize('mobile');
      else if (width < 1024) setScreenSize('tablet');
      else if (width < 1280) setScreenSize('smallDesktop');
      else setScreenSize('desktop');
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch approved codes for button enablement
  const fetchApprovedCodes = useCallback(async () => {
    if (!resourceId) return;

    try {
      const endpoint = isEncounterMode
        ? `/v1/clinical/encounter/${resourceId}/codes`
        : `/v1/clinical/${resourceId}/codes`;

      const response = await apiService.get<{
        success: boolean;
        data: { codes: any[] };
      }>(endpoint);

      if (response.success) {
        setApprovedCodes(response.data.codes || []);
      }
    } catch (error) {
      console.error('Error fetching approved codes:', error);
    }
  }, [resourceId, isEncounterMode]);

  // Fetch clinical summary
  const fetchClinicalSummary = useCallback(async () => {
    if (!resourceId) return;

    try {
      setSummaryLoading(true);
      setSummaryError(null);

      // Use conditional endpoint based on mode
      const endpoint = isEncounterMode
        ? `/v1/clinical/encounter/${resourceId}/summary`
        : `/v1/clinical/${resourceId}/summary`;

      const response = await apiService.get<{
        success: boolean;
        data: ClinicalSummaryData;
      }>(endpoint);

      if (response.success) {
        setClinicalSummaryData(response.data);
      } else {
        throw new Error('Failed to fetch clinical summary');
      }
    } catch (error) {
      console.error('Error fetching clinical summary:', error);
      setSummaryError(error instanceof Error ? error.message : 'Failed to load clinical summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [resourceId, isEncounterMode]);

  // Load validation data
  const loadValidationData = useCallback(async (): Promise<TranscriptionGate | null> => {
    if (!resourceId || !user) return null;

    try {
      setLoading(true);
      setError(null);
      setAccessDenied(false);

      // Use conditional endpoint based on mode
      const endpoint = isEncounterMode
        ? `/v1/clinical/encounter/${resourceId}/data`
        : `/v1/clinical/${resourceId}/data`;

      // Use the correct clinical endpoint
      const response = await apiService.get<{
        success: boolean;
        data: {
          appointment: any;
          encounter: any;
          transcript: any;
          suggestions: any[];
          medicalEntities?: any[];
          chiefComplaint?: any;
          safetyAlerts?: any[];
          conflicts?: any[];
          validations: any[];
          progress: any;
          state: string;
          // ValidationState deprecated - using entity.status directly
        };
      }>(endpoint);

      if (response.success) {
        // Medical entities deprecated - clinical data now flows through clinicalSummary sections
        const adaptedData = {
          appointment: response.data.appointment,
          encounter: response.data.encounter,
          transcript: response.data.transcript || { segments: [], metadata: {} },
          medicalEntities: [], // Deprecated: use clinicalSummary sections
          chiefComplaint: response.data.chiefComplaint,
          safetyAlerts: response.data.safetyAlerts || [],
          conflicts: response.data.conflicts || []
        };
        setValidationData(adaptedData);
        const gate = isEncounterMode
          ? 'ready'
          : transcriptionGateFor(response.data.appointment, response.data.transcript);
        setTranscriptionGate(gate);
        return gate;
      } else {
        throw new Error('Failed to load validation data');
      }
    } catch (err) {
      if (isRecordAccessDenied(err)) {
        setAccessDenied(true);
        return null;
      }
      console.error('Error loading validation data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load validation data');
      return null;
    } finally {
      setLoading(false);
    }
  }, [resourceId, isEncounterMode, user]);

  // Save validation functionality removed per user request

  // Auto-save removed - validation is saved on user actions only
  // No need for periodic saves since state is managed optimistically

  // Initialize data loading - only run once on mount or when resourceId changes
  useEffect(() => {
    if (isEncounterMode) {
      loadValidationData();
      fetchApprovedCodes();
      fetchClinicalSummary();
      return;
    }
    // Appointment mode: codes and the summary need the transcript, so ask once it exists
    loadValidationData().then((gate) => {
      if (gate === 'ready') {
        fetchApprovedCodes();
        fetchClinicalSummary();
      }
    });
  }, [resourceId]);
  // Note: We intentionally don't include loadValidationData, fetchApprovedCodes, and fetchClinicalSummary as dependencies
  // to avoid infinite loops. They are stable functions that only depend on resourceId.

  // 🆕 Listen for entity enrichment events and update only the specific entity (no full page refresh)
  useEffect(() => {
    const handleEntityEnriched = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { entityId, data, improvement } = customEvent.detail;

      console.log('🔄 Entity enriched, updating specific card...', { entityId, data });

      // 🆕 Update only the specific entity instead of full reload
      if (validationData && entityId) {
        setValidationData(prev => {
          if (!prev) return prev;

          return {
            ...prev,
            medicalEntities: prev.medicalEntities.map(entity => {
              if (entity.id === entityId) {
                // Update the enriched entity with new data
                return {
                  ...entity,
                  composite: entity.composite ? {
                    ...entity.composite,
                    ...(data.data?.enrichment?.canonicalQuery && { canonicalQuery: data.data.enrichment.canonicalQuery }),
                    ...(data.data?.enrichment?.queryVariations && { queryVariations: data.data.enrichment.queryVariations }),
                    ...(data.data?.enrichment?.specificityScore !== undefined && { specificityScore: data.data.enrichment.specificityScore }),
                    ...(data.data?.enrichment?.enrichedAt && { enrichedAt: data.data.enrichment.enrichedAt })
                  } : entity.composite,
                  aiDraftCodes: data.data?.aiDraftCodes
                    ? {
                        ...data.data.aiDraftCodes,
                        source: 'ai_draft' as const,
                        generatedAt: new Date().toISOString()
                      }
                    : entity.aiDraftCodes,
                  updatedAt: new Date()
                } as typeof entity;
              }
              return entity;
            })
          };
        });

        console.log(`✅ Entity ${entityId} updated locally (no full refresh)`);
      }

      // 🆕 Show improvement notification if significant
      if (improvement && improvement.newSpecificity > improvement.previousSpecificity + 0.2) {
        const improvementPercent = Math.round((improvement.newSpecificity - improvement.previousSpecificity) * 100);
        console.log(`🎯 Precision improved: ${Math.round(improvement.previousSpecificity * 100)}% → ${Math.round(improvement.newSpecificity * 100)}% (+${improvementPercent}%)`);
      }

      if (improvement && improvement.aiCodesCount > 0) {
        console.log(`✨ ${improvement.aiCodesCount} AI draft codes generated`);
      }
    };

    window.addEventListener('entity-enriched', handleEntityEnriched);

    return () => {
      window.removeEventListener('entity-enriched', handleEntityEnriched);
    };
  }, [validationData]); // Changed dependency from loadValidationData to validationData

  // 🆕 Listen for canonical query acceptance events and update only the specific entity
  useEffect(() => {
    const handleCanonicalAccepted = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { entityId, data } = customEvent.detail;

      console.log('🔄 Canonical query accepted, updating IMO validation...', { entityId, data });

      // Update only the specific entity with IMO validation results
      if (validationData && entityId) {
        setValidationData(prev => {
          if (!prev) return prev;

          return {
            ...prev,
            medicalEntities: prev.medicalEntities.map(entity => {
              if (entity.id === entityId) {
                // Update the entity with IMO validation data
                return {
                  ...entity,
                  imoValidation: data.data?.imoValidation || entity.imoValidation,
                  updatedAt: new Date()
                };
              }
              return entity;
            })
          };
        });

        console.log(`✅ Entity ${entityId} IMO validation updated locally (no full refresh)`);
      }
    };

    window.addEventListener('canonical-accepted', handleCanonicalAccepted);

    return () => {
      window.removeEventListener('canonical-accepted', handleCanonicalAccepted);
    };
  }, [validationData]);

  // Removed auto-refresh - not needed since user actions provide immediate feedback
  // and medical validation is typically done by one physician at a time

  // Handle entity updates - can be re-enabled when MedicalEntitiesPanel supports it
  // const handleEntityUpdate = useCallback((entityId: string, updates: Partial<MedicalEntityWithSource>) => {
  //   if (!validationData) return;
  //   setValidationData(prev => {
  //     if (!prev) return prev;
  //     return {
  //       ...prev,
  //       medicalEntities: prev.medicalEntities.map(entity => 
  //         entity.id === entityId 
  //           ? { ...entity, ...updates, updatedAt: new Date() }
  //           : entity
  //       ),
  //       stats: {
  //         ...prev.stats,
  //         // Update validation counts dynamically
  //         pendingReview: prev.medicalEntities.filter(e => 
  //           e.id === entityId ? updates.entity?.status === 'pending' : e.entity.status === 'pending'
  //         ).length,
  //         approved: prev.medicalEntities.filter(e => 
  //           e.id === entityId ? updates.entity?.status === 'approved' : e.entity.status === 'approved'
  //         ).length,
  //         modified: prev.medicalEntities.filter(e => 
  //           e.id === entityId ? updates.entity?.status === 'modified' : e.entity.status === 'modified'
  //         ).length
  //       }
  //     };
  //   });
  // }, [validationData]);

  // Check EHR delivery availability on mount
  useEffect(() => {
    notesService.checkEhrDeliveryAvailability()
      .then(result => setEhrDeliveryAvailable(result.available))
      .catch(() => setEhrDeliveryAvailable(false));
  }, []);

  // Read the note's EHR delivery status, so a delivery that failed after it was queued shows
  // here and Send stays usable to retry it (AMP-109). A failed read keeps what we last knew.
  // Only the newest read may land: a slow earlier one must not undo what a later one reported.
  const ehrDeliveryReadRef = useRef(0);
  const refreshEhrDelivery = useCallback(async () => {
    if (!noteId || !resourceId) return;
    const read = ++ehrDeliveryReadRef.current;
    const state = await notesService.getEhrDeliveryState(
      noteId,
      isEncounterMode ? { encounterId: resourceId } : { appointmentId: resourceId }
    );
    if (state && read === ehrDeliveryReadRef.current) setEhrDelivery(state);
  }, [noteId, resourceId, isEncounterMode]);

  useEffect(() => {
    if (noteId && ehrDeliveryAvailable) void refreshEhrDelivery();
  }, [noteId, ehrDeliveryAvailable, refreshEhrDelivery]);

  // While the worker holds the delivery, keep reading until it lands or fails.
  const ehrInFlight = ehrDelivery?.status === 'queued' || ehrDelivery?.status === 'delivering';
  useEffect(() => {
    if (!ehrInFlight) return;
    const timer = setInterval(() => { void refreshEhrDelivery(); }, EHR_DELIVERY_POLL_MS);
    return () => clearInterval(timer);
  }, [ehrInFlight, refreshEhrDelivery]);

  const handleNoteGenerated = useCallback((timestamp: string) => {
    setNoteGeneratedAt(timestamp);
    // Telemetry: physician triggered note generation — treat as accepting the current plan section
    telemetry.recordAcceptSection('plan');
  }, [telemetry]);

  // Callback when MedicalNotePreview loads or generates a note
  const handleNoteLoaded = useCallback((loadedNoteId: string, metadata: MedicalNoteMetadata) => {
    setNoteId(loadedNoteId);
    setNoteStatus(metadata?.status || 'draft');
  }, []);

  // Guard: block signing while E&M pipeline has returned ambiguous variants
  // the physician hasn't resolved yet.
  const emVariantsPending = useCodingAssistantStore((s) =>
    resourceId ? Boolean(s.emVariantsPending[resourceId]) : false,
  );

  // Speaker L2 correction state
  const [signBlockOpen, setSignBlockOpen] = useState(false);

  const speakerAnalysis = (validationData?.transcript as any)?.speakerAnalysis ?? null;
  const corrections = useSpeakerCorrectionStore((s) => s.corrections);
  const loadCorrections = useSpeakerCorrectionStore((s) => s.loadCorrections);

  // Preload corrections on mount so hasUnresolvedFlags / autoCorrectCount
  // reflect prior physician work BEFORE the SpeakerReviewPanel is opened.
  // Without this, the sign-block modal raises false alarms on encounters
  // whose merges were already resolved, and AutoCorrectBanner stays hidden
  // until the panel is opened at least once.
  useEffect(() => {
    if (!resourceId || !speakerAnalysis) return;
    loadCorrections(resourceId).catch(() => {
      // Non-fatal: panel will retry on open. Avoid blocking the page.
    });
  }, [resourceId, speakerAnalysis, loadCorrections]);

  const timelineSegments = useMemo(() => {
    const segs = (validationData?.transcript?.segments ?? []) as any[];
    return segs.map((s, i) => ({
      id: s.id,
      index: i + 1,
      speakerId: s.speakerId ?? s.speaker?.id ?? 'Unknown',
      speakerDisplayName: s.speakerDisplayName ?? s.speaker?.displayName ?? 'Unknown',
      text: s.text,
      startTime: Number(s.startTime ?? s.start ?? 0),
      endTime: Number(s.endTime ?? s.end ?? 0),
    }));
  }, [validationData]);

  const indexToSegmentId = useMemo(() => {
    const m = new Map<number, string>();
    timelineSegments.forEach((s) => m.set(s.index, s.id));
    return m;
  }, [timelineSegments]);

  const { flaggedCount, hasUnresolvedFlags, unresolvedSuspectedMerges } =
    useSpeakerAnalysis(speakerAnalysis, corrections, indexToSegmentId);

  const autoCorrectCount = corrections.filter((c) => c.actionType === 'auto_correct').length;
  const hasHumanRequired = (speakerAnalysis?.suspectedMerges ?? [])
    .some((m: any) => m.escalationLevel === 'human_required');
  const autoCorrectsForModal = corrections
    .filter((c) => c.actionType === 'auto_correct')
    .map((c) => {
      const before = (Object.values(c.beforeState)[0] as any) ?? null;
      const after = (Object.values(c.afterState)[0] as any) ?? null;
      const merge = (speakerAnalysis?.suspectedMerges ?? [])
        .find((m: any) => before && m.azureSpeakerId === before.speakerId);
      return {
        fromSpeakerId: before?.speakerId ?? '?',
        toSpeakerId: after?.speakerId ?? '?',
        segmentCount: c.segmentIds.length,
        convergenceScore: merge?.convergenceScore ?? 0,
      };
    });

  const speakers = useMemo(() => {
    const acc = new Map<string, { speakerId: string; role: any; displayName: string; segmentCount: number; confidence: number }>();
    for (const s of timelineSegments) {
      const existing = acc.get(s.speakerId);
      if (existing) {
        existing.segmentCount += 1;
      } else {
        const fromAnalysis = speakerAnalysis?.speakers?.find((sp: any) => sp.speakerId === s.speakerId);
        acc.set(s.speakerId, {
          speakerId: s.speakerId,
          role: (fromAnalysis?.role as any) ?? 'other',
          displayName: s.speakerDisplayName,
          segmentCount: 1,
          confidence: fromAnalysis?.confidence ?? 1,
        });
      }
    }
    return Array.from(acc.values());
  }, [timelineSegments, speakerAnalysis]);

  // Combined handler: finish (sign) note + send to EHR
  const handleFinishAndSend = async () => {
    if (!noteId || !resourceId || isReadOnly()) return;
    if (emVariantsPending) {
      toast.error('Please select a patient type for the E&M code before signing.');
      return;
    }
    setFinishing(true);
    try {
      // Step 1: Finish (sign) the note if not already signed
      if (noteStatus !== 'signed') {
        const signEndpoint = isEncounterMode
          ? `/v1/clinical/encounter/${resourceId}/medical-note/${noteId}/sign`
          : `/v1/clinical/${resourceId}/medical-note/${noteId}/sign`;
        await apiService.post(signEndpoint, {});
        setNoteStatus('signed');
        // Telemetry: physician signed the note — final acceptance of treatment plan
        telemetry.recordAcceptSection('treatment');
      }

      // Step 2: Send to EHR
      const params = isEncounterMode
        ? { encounterId: resourceId }
        : { appointmentId: resourceId };
      const result = await notesService.sendToEhr(noteId, params);

      // Mark sent only for what the server reported, then confirm with a fresh read (AMP-109).
      if (result.status === 'failed') {
        setEhrDelivery({ status: 'failed', error: result.error ?? null, canRetry: true, pullOnly: false, sentWithoutStatus: false });
        toast.error('The note is signed but was not delivered to the EHR. Select Retry to send it again.');
      } else if (result.status === 'no_webhook') {
        setEhrDelivery({ status: 'none', error: null, canRetry: false, pullOnly: true, sentWithoutStatus: false });
        toast('Note finished. No webhook configured \u2014 results available via Pull API.', { icon: '\u2139\uFE0F' });
      } else if (result.status === 'queued') {
        setEhrDelivery({ status: 'queued', error: null, canRetry: false, pullOnly: false, sentWithoutStatus: false });
        toast.success('Note finished and queued for delivery to the EHR');
      } else if (result.status === 'delivered') {
        setEhrDelivery({ status: 'delivered', error: null, canRetry: false, pullOnly: false, sentWithoutStatus: false });
        toast.success('Note finished and sent to EHR successfully');
      }
      await refreshEhrDelivery();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to finish and send to EHR');
    } finally {
      setFinishing(false);
    }
  };

  // Standalone: sign the note only. Same gates as Finish & Send, no EHR delivery afterward.
  const handleSignNote = async () => {
    if (!noteId || !resourceId || !canSignStandalone || noteStatus === 'signed') return;
    if (emVariantsPending) {
      toast.error('Please select a patient type for the E&M code before signing.');
      return;
    }
    setSigning(true);
    try {
      await apiService.post(`/v1/clinical/${resourceId}/medical-note/${noteId}/sign`, {});
      setNoteStatus('signed');
      // Telemetry: physician signed the note — final acceptance of treatment plan
      telemetry.recordAcceptSection('treatment');
      toast.success('Note signed');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to sign the note');
    } finally {
      setSigning(false);
    }
  };

  // Speaker flags open the sign-block modal first; otherwise confirm the chosen action.
  const hasSpeakerSignBlock = hasUnresolvedFlags || hasHumanRequired || autoCorrectsForModal.length > 0;
  const proceedAfterSpeakerReview = canSignStandalone ? handleSignNote : handleFinishAndSend;
  const isNoteSigned = noteStatus === 'signed';

  // Signed and sent are separate (AMP-109): Send stays usable for a signed note that never left or
  // whose delivery failed. Until the status is read, a signed note counts as sent, as before.
  const ehrFailed = ehrDelivery?.status === 'failed';
  const ehrDone = ehrDelivery
    ? ehrDelivery.pullOnly || ehrDelivery.sentWithoutStatus || ehrInFlight || ehrDelivery.status === 'delivered'
    : isNoteSigned;
  const ehrButtonColor = ehrInFlight
    ? 'bg-slate-500'
    : ehrDone
      ? 'bg-green-600'
      : ehrFailed
        ? 'bg-red-600 hover:bg-red-700'
        : 'bg-purple-600 hover:bg-purple-700';
  const EhrButtonIcon = ehrInFlight ? Clock : ehrDone ? CheckCircle2 : ehrFailed ? AlertTriangle : Send;
  // The stored error can be an internal exception message; the detail belongs in Operations.
  const ehrFailedTitle = 'Not delivered to the EHR. Send again to retry.';

  // Handle UI state updates
  const updateUIState = useCallback((updates: Partial<ValidationUIState>) => {
    setUIState(prev => ({ ...prev, ...updates }));
  }, []);

  // Handle entity selection from Medical Intelligence panel
  const handleEntitySelect = useCallback((entityId: string) => {
    // Find the entity and its source information
    const selectedEntity = validationData?.medicalEntities.find(e => e.id === entityId);
    
    if (selectedEntity && selectedEntity.source) {
      // Update UI state to highlight both the entity and the transcript segment
      setUIState(prev => ({
        ...prev,
        selectedEntityId: entityId,
        highlightedSegmentId: selectedEntity.source.segmentId
      }));
    } else {
      // Just select the entity without highlighting if no source info
      setUIState(prev => ({
        ...prev,
        selectedEntityId: entityId
      }));
    }
  }, [validationData]);

  // State for triggering coding assistant refresh
  const [codingRefreshKey, setCodingRefreshKey] = useState(0);
  
  // Function to trigger coding assistant refresh
  const triggerCodingRefresh = useCallback(() => {
    setCodingRefreshKey(prev => prev + 1);
  }, []);

  // Handle entity review (approve/reject/partial)
  const handleEntityReview = useCallback(async (
    entityId: string,
    action: 'approved' | 'rejected' | 'partial' | 'pending',
    comment?: string,
    modifiedText?: string
  ) => {
    if (!resourceId || !validationData) return;

    // Prevent editing if user has read-only access
    if (isReadOnly()) {
      toast.error('Read-only access - Entity review not permitted');
      return;
    }

    // 1. OPTIMISTIC UPDATE - Immediate UI feedback
    const originalData = validationData;
    setValidationData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        medicalEntities: prev.medicalEntities.map(e =>
          e.id === entityId
            ? {
                ...e,
                entity: {
                  ...e.entity,
                  status: action === 'approved' ? 'approved' :
                         action === 'rejected' ? 'rejected' :
                         action === 'pending' ? 'pending' : 'modified',
                  text: modifiedText || e.entity.text
                },
                comment,
                modifiedText
              }
            : e
        )
      };
    });

    // 2. HTTP REQUEST - Persist to backend
    try {
      const requestBody: any = { status: action };
      if (comment) requestBody.comment = comment;
      if (modifiedText && action === 'partial') requestBody.correctedText = modifiedText;

      // Use conditional endpoint based on mode
      const endpoint = isEncounterMode
        ? `/v1/clinical/encounter/${resourceId}/entities/${entityId}/review`
        : `/v1/clinical/${resourceId}/entities/${entityId}/review`;

      const response = await apiService.post<{ success: boolean; data: any }>(
        endpoint,
        requestBody
      );
      
      if (response.success) {
        // 3. SUCCESS - Optimistic update was correct, show feedback
        
        // 🔄 TRIGGER CODING ASSISTANT REFRESH
        // This will cause CodingAssistant to refetch codes from the database
        // to show the impact of the entity status change on medical codes
        setTimeout(() => {
          triggerCodingRefresh();
          fetchApprovedCodes(); // Refresh approved codes for button enablement
        }, 500); // Small delay to allow backend processing to complete
      } else {
        throw new Error('Backend request failed');
      }
    } catch (error) {
      // 4. ROLLBACK - Restore original state and refetch
      console.error('❌ Review failed, rolling back:', error);
      setValidationData(originalData);
      setError('Failed to save review - please try again');


      // Refetch data to ensure consistency
      setTimeout(() => {
        loadValidationData();
        setError(null);
      }, 1000);
    }
  }, [resourceId, isEncounterMode, validationData, loadValidationData, triggerCodingRefresh]);

  // Handle transcript segment selection
  const handleSegmentSelect = useCallback((segmentId: string) => {
    // Clear entity selection when manually selecting a segment
    setUIState(prev => ({
      ...prev,
      highlightedSegmentId: segmentId,
      selectedEntityId: undefined
    }));
  }, []);

  // Transcript not ready yet: look again without the full-page loading screen
  const handleCheckTranscriptAgain = async () => {
    if (!resourceId) return;
    setCheckingTranscript(true);
    try {
      const response = await apiService.get<{ success: boolean; data: any }>(`/v1/clinical/${resourceId}/data`);
      if (!response.success) return;
      const gate = transcriptionGateFor(response.data.appointment, response.data.transcript);
      if (gate === 'ready') {
        await loadValidationData();
        fetchApprovedCodes();
        fetchClinicalSummary();
      } else {
        setTranscriptionGate(gate);
      }
    } catch (err) {
      console.error('Error checking transcription status:', err);
      toast.error('Could not check the transcription. Please try again.');
    } finally {
      setCheckingTranscript(false);
    }
  };

  // Handle navigation - go back to encounters in connected mode, appointments in standalone
  const handleBack = () => {
    navigate(isEncounterMode ? '/encounters' : '/appointments');
  };

  // Apple-inspired loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50/50">
        <motion.div 
          className="bg-white/95 backdrop-blur-3xl p-12 rounded-3xl border border-gray-200/50 shadow-xl text-center max-w-sm mx-4"
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ 
            duration: 0.4,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          <div className="relative mb-6">
            <div className="w-16 h-16 mx-auto bg-blue-500/10 rounded-full flex items-center justify-center">
              <motion.div
                className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Validation</h3>
          <p className="text-sm text-gray-600">Preparing clinical data...</p>
        </motion.div>
      </div>
    );
  }

  if (accessDenied) {
    return <RecordAccessDenied backTo={isEncounterMode ? '/encounters' : '/appointments'} />;
  }

  // Apple-inspired error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50/50">
        <motion.div 
          className="bg-white/95 backdrop-blur-3xl p-12 rounded-3xl border border-gray-200/50 shadow-xl text-center max-w-md mx-4"
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ 
            duration: 0.4,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          <motion.div
            className="w-16 h-16 mx-auto bg-red-500/10 rounded-full flex items-center justify-center mb-6"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <AlertTriangle className="text-red-600" size={24} />
          </motion.div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Unable to Load Data
          </h3>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">{error}</p>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button 
              onClick={loadValidationData} 
              className="bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-200 rounded-xl font-medium px-6 py-2.5"
            >
              <RefreshCw className="mr-2" size={16} />
              Try Again
            </Button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  if (!validationData) {
    return null;
  }

  if (!isEncounterMode && transcriptionGate !== 'ready') {
    const showRecordingLink = canRecord === true && canEditClinical() && transcriptionGate !== 'processing';
    const gateCopy = {
      not_recorded: {
        title: 'No recording yet',
        message: 'This visit has not been recorded yet, so there is nothing to validate.',
      },
      processing: {
        title: 'Transcribing\u2026',
        message: 'The recording was uploaded and is being transcribed. The summary and the note will be ready once it finishes.',
      },
      failed: {
        title: 'Transcription failed',
        message: canRecord === true && canEditClinical()
          ? 'The recording could not be transcribed. Re-upload it from the recording page.'
          : 'The recording could not be transcribed. Someone who can record this visit needs to re-upload it.',
      },
    }[transcriptionGate];

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50/50 px-4">
        <div className="bg-white/95 p-10 rounded-3xl border border-gray-200/50 shadow-xl text-center max-w-md w-full">
          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 ${
            transcriptionGate === 'failed' ? 'bg-red-500/10' : 'bg-blue-500/10'
          }`}>
            {transcriptionGate === 'failed'
              ? <AlertTriangle className="text-red-600" size={24} />
              : <Clock className="text-blue-600" size={24} />}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{gateCopy.title}</h3>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">{gateCopy.message}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button variant="outline" onClick={handleBack} className="rounded-xl">
              <ArrowLeft className="mr-2" size={16} />
              Back to appointments
            </Button>
            {transcriptionGate === 'processing' && (
              <Button
                onClick={handleCheckTranscriptAgain}
                disabled={checkingTranscript}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl"
              >
                <RefreshCw className={`mr-2 ${checkingTranscript ? 'animate-spin' : ''}`} size={16} />
                {checkingTranscript ? 'Checking...' : 'Check again'}
              </Button>
            )}
            {showRecordingLink && (
              <Link
                to={`/recording/${resourceId}`}
                className="inline-flex items-center h-10 px-4 text-sm font-medium bg-blue-500 hover:bg-blue-600 text-white rounded-xl"
              >
                Go to recording
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { appointment, encounter, transcript, medicalEntities } = validationData;

  // Extract patient and visit info based on mode
  const patientName = isEncounterMode
    ? `${encounter?.patientData.firstName || ''} ${encounter?.patientData.lastName || ''}`.trim()
    : appointment?.patient?.firstName && appointment?.patient?.lastName
      ? `${appointment.patient.firstName} ${appointment.patient.lastName}`
      : appointment?.patient?.patientId || 'Unknown Patient';

  const visitType = isEncounterMode
    ? encounter?.encounterDetails.type || 'Encounter'
    : appointment?.appointmentDetails?.type || 'Visit';

  // Scheduled date + time shown beside the visit type. Read UTC components as-is for EHR
  // pre-localized systems, otherwise localize en-US; drop sentinels and unparseable dates.
  const visitDateTime = (() => {
    const scheduledDate = isEncounterMode
      ? encounter?.encounterDetails?.scheduledDate
      : appointment?.appointmentDetails?.scheduledDate;
    if (!scheduledDate || isSentinelDate(scheduledDate)) return null;
    const parsed = new Date(scheduledDate);
    if (Number.isNaN(parsed.getTime())) return null;
    const externalSystemId = isEncounterMode ? encounter?.externalSystemId : undefined;
    const preLocalized = !!externalSystemId && EHR_PRE_LOCALIZED_SYSTEMS.has(externalSystemId);
    const datePart = preLocalized
      ? formatEhrDate(scheduledDate)
      : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const timePart = preLocalized
      ? formatEhrTime(scheduledDate)
      : parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart} · ${timePart}`;
  })();

  return (
    <ErrorBoundary>
      <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 via-blue-50/20 to-indigo-50/20 overflow-hidden pt-safe-top pb-safe-bottom">
        {/* Header - Compact on mobile, full on desktop */}
        <motion.div
          className="flex-shrink-0 bg-white/95 backdrop-blur-3xl border-b border-gray-200/50 shadow-sm px-4 md:px-6 py-1.5 md:py-2"
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/* Breadcrumbs - Desktop only */}
          {!isMobile && (
            <Breadcrumbs
              items={isEncounterMode ? [
                { label: 'Encounters', href: '/encounters' },
                {
                  label: patientName || 'Encounter Validation',
                  active: true
                }
              ] : [
                { label: 'Appointments', href: '/appointments' },
                {
                  label: patientName,
                  active: true
                }
              ]}
            />
          )}

          {/* Mobile: Compact single-line header */}
          {isMobile ? (
            <div className="flex items-center justify-between gap-2 py-1">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <motion.div whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBack}
                    className="h-11 w-11 p-0 rounded-full flex-shrink-0"
                  >
                    <ArrowLeft size={20} />
                  </Button>
                </motion.div>
                <h1 className="text-base font-semibold text-gray-900 truncate">
                  {patientName}
                </h1>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Finish & Send to EHR - Mobile */}
                {noteId && !isReadOnly() && ehrDeliveryAvailable && (
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (hasUnresolvedFlags || hasHumanRequired || autoCorrectsForModal.length > 0) {
                          setSignBlockOpen(true);
                        } else {
                          setShowConfirmModal(true);
                        }
                      }}
                      disabled={finishing || ehrDone}
                      className={`h-9 px-3 text-white rounded-full text-xs ${ehrButtonColor}`}
                      title={ehrFailed ? ehrFailedTitle : 'Finish note and send to EHR'}
                    >
                      <EhrButtonIcon size={14} className="mr-1" />
                      {finishing ? 'Sending...' : ehrInFlight ? 'Queued' : ehrDone ? 'Sent' : ehrFailed ? 'Retry' : 'Send'}
                    </Button>
                  </motion.div>
                )}

                {/* Sign note - Mobile (standalone, no EHR delivery) */}
                {noteId && canSignStandalone && (
                  <motion.div whileTap={{ scale: 0.95 }}>
                    <Button
                      size="sm"
                      onClick={() => (hasSpeakerSignBlock ? setSignBlockOpen(true) : setShowSignConfirm(true))}
                      disabled={signing || isNoteSigned}
                      className={`h-9 px-3 text-white rounded-full text-xs ${isNoteSigned ? 'bg-green-600' : 'bg-purple-600 hover:bg-purple-700'}`}
                      title="Sign the note"
                    >
                      <CheckCircle2 size={14} className="mr-1" />
                      {signing ? 'Signing...' : isNoteSigned ? 'Signed' : 'Sign'}
                    </Button>
                  </motion.div>
                )}

                {/* Info button to expand patient details */}
                <motion.div whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPatientDetails(!showPatientDetails)}
                    className={`h-11 w-11 p-0 rounded-full flex-shrink-0 transition-colors ${
                      showPatientDetails ? 'bg-blue-100 text-blue-600' : ''
                    }`}
                  >
                    <ChevronDown
                      size={20}
                      className={`transition-transform duration-200 ${showPatientDetails ? 'rotate-180' : ''}`}
                    />
                  </Button>
                </motion.div>
              </div>
            </div>
          ) : (
            /* Desktop: Full header with title and pills */
            <div className="flex items-center justify-between mt-2 gap-3">
              <div className="flex items-center gap-4">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    className="bg-gray-100/80 hover:bg-gray-200/80 border-gray-200/50 text-gray-700 hover:text-gray-900 shadow-sm transition-all duration-200 h-9 rounded-xl"
                  >
                    <ArrowLeft className="mr-2" size={16} />
                    Back
                  </Button>
                </motion.div>

                <div>
                  <h1 className="text-lg font-semibold text-gray-900 mb-1">
                    Clinical Validation
                    {patientName && (
                      <span className="text-gray-400 font-normal mx-2">&mdash;</span>
                    )}
                    {patientName && (
                      <span className="text-blue-700">{patientName}</span>
                    )}
                  </h1>

                  {/* Context Information - Apple-style pills */}
                  <div className="flex items-center gap-2">
                    {/* Visit Type */}
                    <div className="flex items-center gap-2 px-3 py-1 bg-purple-50/80 rounded-full border border-purple-100/50">
                      <Calendar className="text-purple-600" size={12} />
                      <span className="text-sm font-medium text-gray-700">
                        {visitType}
                      </span>
                    </div>

                    {/* Scheduled date + time */}
                    {visitDateTime && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-slate-50/80 rounded-full border border-slate-200/60">
                        <Clock className="text-slate-500" size={12} />
                        <span className="text-sm font-medium text-gray-700">
                          {visitDateTime}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Finish & Send to EHR */}
              {noteId && !isReadOnly() && ehrDeliveryAvailable && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    onClick={() => {
                      if (hasUnresolvedFlags || hasHumanRequired || autoCorrectsForModal.length > 0) {
                        setSignBlockOpen(true);
                      } else {
                        setShowConfirmModal(true);
                      }
                    }}
                    disabled={finishing || ehrDone}
                    className={`flex items-center gap-1.5 text-white shadow-sm rounded-xl h-9 px-4 transition-all duration-200 ${ehrButtonColor}`}
                    title={ehrFailed ? ehrFailedTitle : 'Finish note and send clinical results to connected EHR'}
                  >
                    <EhrButtonIcon size={14} />
                    {finishing
                      ? 'Sending...'
                      : ehrInFlight
                        ? 'Queued for EHR'
                        : ehrDone
                          ? 'Sent'
                          : ehrFailed
                            ? 'Retry Send to EHR'
                            : isNoteSigned
                              ? 'Send to EHR'
                              : 'Finish & Send to EHR'}
                  </Button>
                </motion.div>
              )}

              {/* Sign note (standalone, no EHR delivery) */}
              {noteId && canSignStandalone && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    onClick={() => (hasSpeakerSignBlock ? setSignBlockOpen(true) : setShowSignConfirm(true))}
                    disabled={signing || isNoteSigned}
                    className={`flex items-center gap-1.5 text-white shadow-sm rounded-xl h-9 px-4 transition-all duration-200 ${isNoteSigned ? 'bg-green-600' : 'bg-purple-600 hover:bg-purple-700'}`}
                    title="Sign the note. A signed note can no longer be edited."
                  >
                    <CheckCircle2 size={14} />
                    {signing ? 'Signing...' : isNoteSigned ? 'Signed' : 'Sign note'}
                  </Button>
                </motion.div>
              )}
            </div>
          )}

          {/* Mobile: Expandable patient details */}
          <AnimatePresence>
            {isMobile && showPatientDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="flex flex-wrap gap-2 py-2 border-t border-gray-100 mt-1">
                  {/* Patient Info */}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50/80 rounded-full border border-blue-100/50">
                    <User className="text-blue-600" size={12} />
                    <span className="text-xs font-medium text-gray-700">
                      {patientName}
                    </span>
                  </div>

                  {/* Visit Type */}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50/80 rounded-full border border-purple-100/50">
                    <Calendar className="text-purple-600" size={12} />
                    <span className="text-xs font-medium text-gray-700">
                      {visitType}
                    </span>
                  </div>

                  {/* Scheduled date + time */}
                  {visitDateTime && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50/80 rounded-full border border-slate-200/60">
                      <Clock className="text-slate-500" size={12} />
                      <span className="text-xs font-medium text-gray-700">
                        {visitDateTime}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Safety Alerts - Apple-inspired design */}
        <AnimatePresence>
          {validationData.safetyAlerts.length > 0 && (
            <motion.div
              className="flex-shrink-0 px-4 md:px-6 py-3"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div 
                className="bg-red-50/90 backdrop-blur-2xl border border-red-200/50 rounded-2xl p-4 shadow-sm"
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-500/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertTriangle className="text-red-600" size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-red-900 text-sm">Safety Alerts</h4>
                      <div className="px-2 py-0.5 bg-red-500/20 text-red-700 text-xs font-medium rounded-full">
                        {validationData.safetyAlerts.length}
                      </div>
                    </div>
                    <div className="space-y-1">
                      {validationData.safetyAlerts.slice(0, 2).map((alert, index) => (
                        <motion.div 
                          key={index}
                          className="text-sm text-red-800 leading-relaxed"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          {alert.message}
                        </motion.div>
                      ))}
                      {validationData.safetyAlerts.length > 2 && (
                        <motion.div 
                          className="text-xs text-red-600 font-medium mt-2"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.3 }}
                        >
                          +{validationData.safetyAlerts.length - 2} more alerts
                        </motion.div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Speaker L2 auto-correct informational banner */}
        <AutoCorrectBanner
          encounterId={resourceId ?? ''}
          autoCorrectCount={autoCorrectCount}
          onReview={() => useSpeakerCorrectionStore.getState().openPanel()}
        />

        {/* Speaker L2 alert banner */}
        {flaggedCount > 0 && (
          <div className="flex-shrink-0 mx-4 md:mx-6 mb-2 p-3 bg-amber-50 border-l-4 border-amber-500 flex items-center justify-between">
            <span className="text-sm">
              {flaggedCount} speaker {flaggedCount === 1 ? 'alert' : 'alerts'} on this encounter
              {hasUnresolvedFlags ? ' — review before signing' : ' — all addressed'}.
            </span>
            <button
              type="button"
              onClick={() => useSpeakerCorrectionStore.getState().openPanel()}
              className="text-sm px-3 py-1 border rounded bg-white hover:bg-amber-100"
            >
              Review speakers
            </button>
          </div>
        )}

        {/* Speaker Review Panel (renders as a slide-over; visible only when store.isPanelOpen) */}
        <SpeakerReviewPanel
          encounterId={resourceId ?? ''}
          segments={timelineSegments}
          speakers={speakers as any}
          speakerAnalysis={speakerAnalysis}
          onPlaySegment={() => { /* hook into existing audio playback if available */ }}
        />

        {/* Main Content - Fixed height 3-panel layout */}
        <div className="flex-1 flex relative overflow-hidden">
          {/* Mobile Panel Switcher - Compact with swipe support */}
          {isMobile && (
            <motion.div
              className="absolute top-0 left-0 right-0 z-10 bg-white/95 backdrop-blur-xl border-b border-gray-200/50 shadow-sm"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {/* Compact Segmented Control */}
              <div className="flex p-1.5 mx-2 bg-gray-100/80 rounded-lg">
                {[
                  { id: 'transcript' as PanelId, label: 'Transcript', icon: MessageSquare },
                  { id: 'soap' as PanelId, label: 'Note', icon: FileText },
                  { id: 'sidebar' as PanelId, label: 'Codes', icon: Activity }
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setShowMobilePanel(id)}
                    aria-label={`Show ${label} panel`}
                    aria-pressed={showMobilePanel === id}
                    className={`flex-1 py-2 px-2 rounded-md text-xs font-medium transition-all duration-200 flex items-center justify-center gap-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
                      showMobilePanel === id
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <Icon size={14} />
                    <span>{label}</span>
                  </button>
                ))}
              </div>

            </motion.div>
          )}
          
          {/* Left Panel - Transcript Viewer */}
          <motion.div
            className={`${
              isMobile
                ? `absolute inset-0 pt-14 ${showMobilePanel !== 'transcript' ? 'hidden' : ''}`
                : isTablet
                  ? 'w-1/2 border-r border-gray-200/50'
                  : isSmallDesktop
                    ? 'w-[42%] border-r border-gray-200/50'
                    : 'w-[38%] border-r border-gray-200/50'
            } bg-white/90 backdrop-blur-3xl flex flex-col shadow-sm`}
            initial={isMobile ? { x: -100, opacity: 0 } : { x: -20, opacity: 0 }}
            animate={isMobile ? { x: 0, opacity: 1 } : { x: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* Panel Header - Desktop only */}
            {!isMobile && (
              <div className="flex-shrink-0 border-b border-gray-200/50 bg-white/95 backdrop-blur-2xl">
                <div className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-500/10 rounded-full flex items-center justify-center">
                        <MessageSquare className="text-blue-500" size={16} />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">Conversation</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {transcript.segments?.length || 0} segments
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tab Switcher */}
                <div className="px-4 pb-2 flex gap-2">
                  <motion.button
                    onClick={() => setTranscriptView('summary')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
                      transcriptView === 'summary'
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-gray-100/80 text-gray-600 hover:bg-gray-200/80'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FileText size={14} />
                    Summary
                  </motion.button>
                  <motion.button
                    onClick={() => setTranscriptView('transcript')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
                      transcriptView === 'transcript'
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-gray-100/80 text-gray-600 hover:bg-gray-200/80'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <MessageSquare size={14} />
                    Transcript
                  </motion.button>
                </div>
              </div>
            )}

            {/* Scrollable Content */}
            <div
              ref={transcriptScroll.scrollRef}
              className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300/50 scrollbar-track-transparent hover:scrollbar-thumb-gray-400/50"
            >
              <div className={isMobile ? 'p-3' : 'p-6'}>
                {/* Mobile: Inline pill toggle for Transcript/Summary (scrolls with content) */}
                {isMobile && (
                  <div className="flex gap-1.5 mb-3 bg-gray-100/80 p-1 rounded-lg">
                    <button
                      onClick={() => setTranscriptView('summary')}
                      className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                        transcriptView === 'summary'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600'
                      }`}
                    >
                      <FileText size={12} />
                      Summary
                    </button>
                    <button
                      onClick={() => setTranscriptView('transcript')}
                      className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                        transcriptView === 'transcript'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600'
                      }`}
                    >
                      <MessageSquare size={12} />
                      Transcript
                    </button>
                  </div>
                )}
                {transcriptView === 'transcript' ? (
                  <>
                  <AudioPlayer
                    ref={audioPlayerRef}
                    appointmentId={appointmentId}
                    encounterId={encounterId}
                  />
                  <TranscriptViewer
                    segments={transcript.segments?.map((seg: any, idx: number) => {
                    // Map backend speaker string to proper role
                    const getSpeakerData = () => {
                      if (typeof seg.speaker === 'string') {
                        // AMP-72: route through the canonical role config instead of
                        // a hardcoded ladder that silently dropped 'interpreter' (and
                        // would drop any future role) into gray "Unknown Speaker".
                        // normalizeSpeakerRole already handles the 'doctor' alias and
                        // 'Family Member' -> 'family'.
                        const raw = seg.speaker.toLowerCase() === 'family member'
                          ? 'family'
                          : seg.speaker.toLowerCase();
                        const role = normalizeSpeakerRole(raw) !== 'other'
                          ? normalizeSpeakerRole(raw)
                          : normalizeSpeakerRole(seg.speakerRole?.toLowerCase());

                        // Preserve free-text speaker naming: an unrecognized, non-'Unknown'
                        // label stays visible rather than collapsing to "Other".
                        const unmatched = role === 'other';
                        const displayName = unmatched && seg.speaker !== 'Unknown'
                          ? seg.speaker
                          : SPEAKER_ROLE_CONFIG[role].label;

                        return {
                          id: role,
                          role,
                          displayName,
                          confidence: seg.confidence || (unmatched ? 0.5 : 1)
                        };
                      }

                      // Speaker is already an object
                      if (seg.speaker && seg.speaker.role) {
                        return {
                          ...seg.speaker,
                          displayName: seg.speaker.displayName ||
                                      (seg.speaker.role !== 'other'
                                        ? seg.speaker.role.charAt(0).toUpperCase() + seg.speaker.role.slice(1)
                                        : 'Other')
                        };
                      }

                      // No valid speaker data
                      return {
                        id: 'other',
                        role: 'other' as const,
                        displayName: 'Other',
                        confidence: 0
                      };
                    };

                    return {
                      ...seg,
                      id: seg.id || `seg-${idx}`,  // Fallback for older transcripts without IDs
                      speaker: getSpeakerData()
                    };
                  }) || []}
                    highlightedSegmentId={uiState.highlightedSegmentId}
                    selectedEntityId={uiState.selectedEntityId}
                    medicalEntities={medicalEntities}
                    onSegmentSelect={handleSegmentSelect}
                    onSegmentSeek={(time) => audioPlayerRef.current?.seekTo(time)}
                    compact={isMobile}
                  />
                  </>
                ) : (
                  <ClinicalSummaryPanel
                    summary={clinicalSummaryData?.aiGenerated || null}
                    loading={summaryLoading}
                    error={summaryError}
                    appointmentId={resourceId || ''}
                    isEncounterMode={isEncounterMode}
                    noteStatus={noteStatus}
                    onSummaryUpdate={async () => {
                      await fetchClinicalSummary();
                      setSummaryLastSaved(new Date().toISOString());
                    }}
                    onNotesSaved={(sectionKey, richText, plainText) => {
                      // Telemetry: physician saved free-text notes for this section
                      telemetry.recordFreeTextOverride(sectionKey);
                      setClinicalSummaryData(prev => {
                        if (!prev?.aiGenerated) return prev;
                        const section = prev.aiGenerated[sectionKey as keyof ClinicalSummary];
                        if (!section) return prev;
                        const status = deriveSectionStatus(plainText);
                        return {
                          ...prev,
                          aiGenerated: {
                            ...prev.aiGenerated,
                            [sectionKey]: {
                              ...section,
                              status,
                              physicianNotes: {
                                ...section.physicianNotes,
                                richText,
                                plainText,
                                updatedAt: new Date().toISOString(),
                              }
                            }
                          }
                        };
                      });
                      setSummaryLastSaved(new Date().toISOString());
                    }}
                  />
                )}
              </div>
            </div>
          </motion.div>

          {/* Center Panel - Medical Note Preview (+ Coding tab on tablet/smallDesktop) */}
          <motion.div
            className={`${
              isMobile
                ? `absolute inset-0 pt-14 ${showMobilePanel !== 'soap' ? 'hidden' : ''}`
                : isTablet
                  ? 'w-1/2'
                  : isSmallDesktop
                    ? 'w-[58%]'
                    : 'w-[35%] border-r border-gray-200/50'
            } bg-gradient-to-br from-gray-50/80 via-blue-50/30 to-indigo-50/30 backdrop-blur-3xl flex flex-col`}
            initial={isMobile ? { scale: 0.95, opacity: 0 } : { y: 20, opacity: 0 }}
            animate={isMobile ? { scale: 1, opacity: 1 } : { y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* Panel Header with Note/Codes tab switcher for tablet and smallDesktop */}
            {!isMobile && (
              <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200/50 bg-white/95 backdrop-blur-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isTabletOrSmallDesktop && rightPanelView === 'coding'
                        ? 'bg-purple-500/10'
                        : 'bg-blue-500/10'
                    }`}>
                      {isTabletOrSmallDesktop && rightPanelView === 'coding'
                        ? <Activity className="text-purple-500" size={16} />
                        : <FileText className="text-blue-500" size={16} />
                      }
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">
                        {isTabletOrSmallDesktop && rightPanelView === 'coding' ? 'Coding Assistant' : 'Medical Note'}
                      </h3>
                      {!(isTabletOrSmallDesktop) && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Professional medical documentation
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Tab switcher for tablet and smallDesktop */}
                  {isTabletOrSmallDesktop && (
                    <div className="flex bg-gray-100 rounded-lg p-0.5">
                      <button
                        onClick={() => setRightPanelView('note')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
                          rightPanelView === 'note'
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <FileText size={13} />
                        Note
                      </button>
                      <button
                        onClick={() => setRightPanelView('coding')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
                          rightPanelView === 'coding'
                            ? 'bg-white text-purple-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Activity size={13} />
                        Codes
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Scrollable Content - conditionally renders Note or Coding based on tab */}
            <div
              ref={entitiesScroll.scrollRef}
              className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300/50 scrollbar-track-transparent hover:scrollbar-thumb-gray-400/50"
            >
              <div className={isMobile ? 'p-4' : 'p-4 lg:p-6'}>
                {isTabletOrSmallDesktop && rightPanelView === 'coding' ? (
                  <EnhancedCodingAssistant
                    appointmentId={appointmentId}
                    encounterId={encounterId}
                    isEncounterMode={isEncounterMode}
                    key={`tabbed-${codingRefreshKey}`}
                    appointment={appointment}
                    encounter={encounter}
                    refreshKey={codingRefreshKey}
                    clinicalSummary={clinicalSummaryData}
                    onGenerateCode={() => {}}
                    onCodeStatusChange={() => {
                      fetchApprovedCodes();
                    }}
                  />
                ) : (
                  <MedicalNotePreview
                    appointmentId={resourceId || ''}
                    isEncounterMode={isEncounterMode}
                    hasPendingChanges={hasPendingNoteChanges}
                    onNoteGenerated={handleNoteGenerated}
                    onNoteLoaded={handleNoteLoaded}
                    isSigned={isNoteSigned}
                    onNoteUpdate={async () => {
                      await fetchClinicalSummary();
                    }}
                  />
                )}
              </div>
            </div>
          </motion.div>

          {/* Right Panel - Coding Assistant (standalone, desktop only >=1280px) */}
          <motion.div
            className={`${
              isMobile
                ? `absolute inset-0 pt-14 ${showMobilePanel !== 'sidebar' ? 'hidden' : ''}`
                : isDesktop
                  ? 'flex-1'
                  : 'hidden'
            } bg-white/90 backdrop-blur-3xl flex flex-col shadow-sm`}
            initial={isMobile ? { x: 100, opacity: 0 } : { x: 20, opacity: 0 }}
            animate={isMobile ? { x: 0, opacity: 1 } : { x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* Panel Header - Desktop only */}
            {!isMobile && (
              <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200/50 bg-white/95 backdrop-blur-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-500/10 rounded-full flex items-center justify-center">
                    <Activity className="text-purple-500" size={16} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Coding Assistant</h3>
                  </div>
                </div>
              </div>
            )}
            
            {/* Scrollable Content */}
            <div
              ref={sidebarScroll.scrollRef}
              className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300/50 scrollbar-track-transparent hover:scrollbar-thumb-gray-400/50"
            >
              <div className={isMobile ? 'p-4' : 'p-6'}>
                <EnhancedCodingAssistant
                  appointmentId={appointmentId}
                  encounterId={encounterId}
                  isEncounterMode={isEncounterMode}
                  key={codingRefreshKey}
                  appointment={appointment}
                  encounter={encounter}
                  refreshKey={codingRefreshKey}
                  clinicalSummary={clinicalSummaryData}
                  onGenerateCode={() => {
                    // Handle code generation - placeholder for future functionality
                  }}
                  onCodeStatusChange={() => {
                    // Refresh approved codes when code status changes
                    fetchApprovedCodes();
                  }}
                />
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll to Top Buttons - only on full desktop with 3 separate panels */}
        {isDesktop && (
          <>
            <ScrollToTopButton
              onScrollToTop={transcriptScroll.scrollToTop}
              onScrollToBottom={transcriptScroll.scrollToBottom}
              isAtTop={transcriptScroll.isAtTop()}
              isAtBottom={transcriptScroll.isAtBottom()}
              className="bottom-4 right-4"
            />
            <ScrollToTopButton
              onScrollToTop={entitiesScroll.scrollToTop}
              onScrollToBottom={entitiesScroll.scrollToBottom}
              isAtTop={entitiesScroll.isAtTop()}
              isAtBottom={entitiesScroll.isAtBottom()}
              className="bottom-4 right-16"
            />
            <ScrollToTopButton
              onScrollToTop={sidebarScroll.scrollToTop}
              onScrollToBottom={sidebarScroll.scrollToBottom}
              isAtTop={sidebarScroll.isAtTop()}
              isAtBottom={sidebarScroll.isAtBottom()}
              className="bottom-4 right-28"
            />
          </>
        )}
      </div>

      {/* Modal Portal - Outside of scroll containers */}
      <div id="modal-portal" className="relative z-[9999]" />

      {/* Speaker Sign Block Modal */}
      {signBlockOpen && (
        <SignBlockModal
          unresolvedCount={unresolvedSuspectedMerges.length}
          hasHumanRequired={hasHumanRequired}
          autoCorrects={autoCorrectsForModal}
          onSignAnyway={() => { setSignBlockOpen(false); proceedAfterSpeakerReview(); }}
          onReview={() => { setSignBlockOpen(false); useSpeakerCorrectionStore.getState().openPanel(); }}
          onSignWithReason={async (reason) => {
            try {
              for (const m of unresolvedSuspectedMerges) {
                const mergeId = `${m.azureSpeakerId}:[${m.segmentIndices.join(',')}]`;
                await useSpeakerCorrectionStore.getState().applyCorrection(resourceId ?? '', {
                  actionType: 'skip_with_reason',
                  segmentIds: [],
                  skip: { suspectedMergeId: mergeId, reason },
                });
              }
              setSignBlockOpen(false);
              proceedAfterSpeakerReview();
            } catch (err) {
              setSignBlockOpen(false);
              const msg = err instanceof Error ? err.message : 'Failed to record skip reason';
              toast.error(`Could not save your reason. (${msg})`);
            }
          }}
          onClose={() => setSignBlockOpen(false)}
        />
      )}

      {/* EHR Delivery Confirmation Modal */}
      <EhrDeliveryConfirmModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          setShowConfirmModal(false);
          handleFinishAndSend();
        }}
        confirming={finishing}
        patientName={patientName}
        visitType={visitType}
        clinicalSummary={clinicalSummaryData}
        approvedCodes={approvedCodes}
        noteStatus={noteStatus}
        resourceId={resourceId || ''}
        isEncounterMode={isEncounterMode}
      />

      {/* Standalone sign confirmation: signing locks the summary and the note */}
      <Dialog open={showSignConfirm} onOpenChange={(isOpen) => { if (!isOpen) setShowSignConfirm(false); }}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Sign this note?</DialogTitle>
            <DialogDescription>
              Once signed, the clinical summary and the note for {patientName || 'this patient'} can no longer be edited.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignConfirm(false)} disabled={signing} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={() => { setShowSignConfirm(false); handleSignNote(); }}
              disabled={signing}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
            >
              Sign note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ErrorBoundary>
  );
};